import * as XLSX from 'xlsx';
import type { ImportRow } from '../components/inventory/ImportGreenBeanModal/types';

export const MAX_IMPORT_ROWS = 500;

const HEADER_TO_FIELD: Record<string, keyof ImportRow> = {
  'Bean Name': 'beanName',
  'Description': 'description',
  'Processor Name': 'processorName',
  'Species': 'species',
  'Variety': 'variety',
  'Process Type': 'processType',
  'Initial Quantity (kg)': 'initialQty',
  'Reorder Warning (kg)': 'reorderPoint',
  'Standard Cost': 'standardCost',
};

const REQUIRED_HEADERS = Object.keys(HEADER_TO_FIELD);

const NUMERIC_FIELDS = new Set<keyof ImportRow>([
  'initialQty',
  'reorderPoint',
  'standardCost',
]);

export interface ParseResult {
  rows: ImportRow[];
}

export function parseGreenBeanImportWorkbook(wb: XLSX.WorkBook): ParseResult {
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('Workbook has no sheets');
  const sheet = wb.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json<(string | number)[]>(sheet, {
    header: 1,
    defval: '',
  });
  if (aoa.length === 0) throw new Error('Sheet is empty');

  const headerRow = aoa[0].map((h) => String(h).trim());
  for (const required of REQUIRED_HEADERS) {
    if (!headerRow.includes(required)) {
      throw new Error(`Missing required column: ${required}`);
    }
  }

  const dataRows = aoa.slice(1);
  if (dataRows.length > MAX_IMPORT_ROWS) {
    throw new Error(`Too many rows (max ${MAX_IMPORT_ROWS})`);
  }

  const rows: ImportRow[] = dataRows
    .filter((r) => r.some((cell) => String(cell).trim() !== ''))
    .map((rawRow) => buildRow(headerRow, rawRow));

  return { rows };
}

function buildRow(headers: string[], rawRow: (string | number)[]): ImportRow {
  const out: ImportRow = {
    beanName: '',
    description: '',
    processorName: '',
    species: '',
    variety: '',
    processType: '',
    initialQty: 0,
    reorderPoint: 0,
    standardCost: 0,
  };
  headers.forEach((h, i) => {
    const field = HEADER_TO_FIELD[h];
    if (!field) return;
    const cell = rawRow[i];
    if (NUMERIC_FIELDS.has(field)) {
      const n =
        typeof cell === 'number' ? cell : parseFloat(String(cell).trim());
      (out[field] as number) = Number.isFinite(n) ? n : 0;
    } else {
      const s = String(cell ?? '').trim();
      if (field === 'species') {
        // Normalize to title case (e.g. "arabica" -> "Arabica") so the
        // backend's `oneof` binding accepts it.
        out.species =
          s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
      } else if (field === 'processType') {
        // Render with leading capital for display (washed -> Washed).
        out.processType =
          s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
      } else {
        (out[field] as string) = s;
      }
    }
  });
  return out;
}

export async function parseGreenBeanImportFile(
  file: File,
): Promise<ParseResult> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  return parseGreenBeanImportWorkbook(wb);
}
